import { useEffect, useMemo, useState, type ReactNode } from "react";
import { cn } from "@/client/lib/utils";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/client/components/ui/table";
import { Button } from "@/client/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectPopup, SelectItem } from "@/client/components/ui/select";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronsUpDownIcon,
} from "lucide-react";

// Page numbers with ellipsis windowing (shows all when few pages). Lifted from the proven
// OfferingsPage pattern so every admin table paginates identically.
function pageItems(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const items: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push("…");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < total - 1) items.push("…");
  items.push(total);
  return items;
}

export type DataTableColumn<T> = {
  /** Stable key, also the sort key when sortable. */
  key: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  /** When true, the header becomes a sort toggle; you MUST also provide sortValue. */
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
  cell: (row: T) => ReactNode;
  headClassName?: string;
  cellClassName?: string;
};

type SortState = { key: string; dir: "asc" | "desc" } | null;

const PAGE_SIZE_LABELS = (opts: number[]): Record<string, string> =>
  Object.fromEntries(opts.map((n) => [String(n), String(n)]));

const alignClass = (align?: "left" | "right" | "center") =>
  align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

export function DataTable<T>({
  columns,
  data,
  getRowKey,
  initialSort = null,
  pageSizeOptions = [25, 50, 100],
  defaultPageSize = 25,
  emptyMessage = "Niets gevonden.",
  /** Change this (e.g. to the current filter string) to jump back to page 1. */
  resetKey,
}: {
  columns: DataTableColumn<T>[];
  data: T[];
  getRowKey: (row: T, index: number) => string | number;
  initialSort?: SortState;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  emptyMessage?: ReactNode;
  resetKey?: unknown;
}) {
  const [sort, setSort] = useState<SortState>(initialSort);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    pageSizeOptions.includes(defaultPageSize) ? defaultPageSize : (pageSizeOptions[0] ?? 25),
  );

  // Jump back to page 1 whenever the caller's filter or the page size changes.
  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  const sorted = useMemo(() => {
    if (!sort) return data;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return data;
    const get = col.sortValue;
    const next = [...data].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), "nl", { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return next;
  }, [data, sort, columns]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paged = sorted.slice(pageStart, pageStart + pageSize);

  const toggleSort = (key: string) =>
    setSort((s) => (s && s.key === key && s.dir === "asc" ? { key, dir: "desc" } : { key, dir: "asc" }));

  return (
    <div className="space-y-3">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {columns.map((c) => (
              <TableHead key={c.key} className={cn(alignClass(c.align), c.headClassName)}>
                {c.sortable && c.sortValue ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key)}
                    className={`inline-flex cursor-pointer items-center gap-1 hover:text-foreground ${
                      sort?.key === c.key ? "text-foreground" : ""
                    } ${c.align === "right" ? "flex-row-reverse" : ""}`}
                  >
                    {c.header}
                    {sort?.key === c.key ? (
                      sort.dir === "asc" ? (
                        <ChevronUpIcon size={13} />
                      ) : (
                        <ChevronDownIcon size={13} />
                      )
                    ) : (
                      <ChevronsUpDownIcon size={13} className="opacity-30" />
                    )}
                  </button>
                ) : (
                  c.header
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {total === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center text-muted-foreground">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            paged.map((row, i) => (
              <TableRow key={getRowKey(row, pageStart + i)}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={cn(alignClass(c.align), c.cellClassName)}>
                    {c.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 text-sm">
          <span className="text-muted-foreground tabular-nums">
            Toont {pageStart + 1}–{Math.min(pageStart + pageSize, total)} van {total}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Per pagina</span>
              <Select
                items={PAGE_SIZE_LABELS(pageSizeOptions)}
                value={String(pageSize)}
                onValueChange={(v) => {
                  const n = Number(v);
                  if (Number.isFinite(n) && n > 0) setPageSize(n);
                }}
              >
                <SelectTrigger size="sm" className="w-20 min-w-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectPopup>
                  {pageSizeOptions.map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                  aria-label="Vorige pagina"
                >
                  <ChevronLeftIcon size={15} />
                </Button>
                {pageItems(currentPage, totalPages).map((p, i) =>
                  p === "…" ? (
                    <span key={`e${i}`} className="px-1.5 text-muted-foreground">
                      …
                    </span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === currentPage ? "default" : "outline"}
                      size="icon"
                      onClick={() => setPage(p)}
                      aria-label={`Pagina ${p}`}
                      aria-current={p === currentPage ? "page" : undefined}
                    >
                      {p}
                    </Button>
                  ),
                )}
                <Button
                  variant="outline"
                  size="icon"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                  aria-label="Volgende pagina"
                >
                  <ChevronRightIcon size={15} />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
